import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { formatDate } from '../../data/mockData';
import { useApp } from '../../context/AppContext';
import { Play, Eye, Image } from 'lucide-react';

const ExecutionsTable = ({ executions, onStartExecution }) => {
  const { isReadOnly, canEdit } = useApp();
  const [viewEvidenceOpen, setViewEvidenceOpen] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Overdue': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const handleStart = (execution) => {
    if (onStartExecution) {
      onStartExecution(execution);
    } else {
      // Fallback: open a preview modal
      setSelectedExecution(execution);
      setViewEvidenceOpen(true);
    }
  };

  const handleViewEvidence = (execution) => {
    setSelectedExecution(execution);
    setViewEvidenceOpen(true);
  };

  // Mock evidence data
  const getMockEvidence = (execution) => {
    if (execution.status !== 'Completed') return [];
    return [
      { type: 'photo', label: 'Area Photo', url: '#' },
      { type: 'reading', label: 'Temperature Reading', value: '22.5°C' },
      { type: 'note', label: 'Notes', text: 'All checks completed successfully. No issues observed.' }
    ];
  };

  return (
    <>
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                <TableHead className="text-slate-600">Execution ID</TableHead>
                <TableHead className="text-slate-600">Checklist</TableHead>
                <TableHead className="text-slate-600">Plant/Area</TableHead>
                <TableHead className="text-slate-600">Due Date</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Score</TableHead>
                <TableHead className="text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No executions found
                  </TableCell>
                </TableRow>
              ) : (
                executions.map(exec => (
                  <TableRow key={exec.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs text-slate-500">
                      {exec.id}
                    </TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">
                      {exec.templateName || exec.checklistId}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {exec.plant} / {exec.area || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {exec.dueDate ? formatDate(exec.dueDate) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(exec.status)} text-xs`}>
                        {exec.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {exec.status === 'Completed' && exec.score !== null ? (
                        <div className="flex items-center gap-2">
                          <Progress value={exec.score} className="h-2 w-16 bg-slate-200" />
                          <span className="text-xs text-slate-600">{exec.score}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {exec.status === 'Pending' || exec.status === 'Overdue' ? (
                          canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStart(exec)}
                              className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )
                        ) : exec.status === 'Completed' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewEvidence(exec)}
                            className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Evidence Viewer Dialog */}
      <Dialog open={viewEvidenceOpen} onOpenChange={setViewEvidenceOpen}>
        <DialogContent className="sm:max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              Execution Evidence - {selectedExecution?.id}
            </DialogTitle>
          </DialogHeader>
          {selectedExecution && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Checklist</p>
                    <p className="text-slate-800">{selectedExecution.templateName || selectedExecution.checklistId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Executed By</p>
                    <p className="text-slate-800">{selectedExecution.executedBy || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Date</p>
                    <p className="text-slate-800">
                      {selectedExecution.executedAt ? formatDate(selectedExecution.executedAt) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Score</p>
                    <p className="text-slate-800">{selectedExecution.score || selectedExecution.passRate || 0}%</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Evidence</h3>
                  {getMockEvidence(selectedExecution).length > 0 ? (
                    <div className="space-y-3">
                      {getMockEvidence(selectedExecution).map((evidence, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                          {evidence.type === 'photo' && (
                            <div className="flex items-center gap-2">
                              <Image className="h-4 w-4 text-slate-500" />
                              <span className="text-sm text-slate-700">{evidence.label}</span>
                              <span className="text-xs text-slate-500 ml-auto">(Mock image)</span>
                            </div>
                          )}
                          {evidence.type === 'reading' && (
                            <div>
                              <p className="text-sm font-medium text-slate-700">{evidence.label}</p>
                              <p className="text-sm text-slate-600">{evidence.value}</p>
                            </div>
                          )}
                          {evidence.type === 'note' && (
                            <div>
                              <p className="text-sm font-medium text-slate-700">{evidence.label}</p>
                              <p className="text-sm text-slate-600">{evidence.text}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No evidence available</p>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExecutionsTable;

